import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRightLeft, Check, Clock, Box, Fuel, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { getTransaction } from '../lib/api';
import { formatNumber, formatDate, timeAgo, getTxTypeColor } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

export default function TransactionDetail() {
  const { hash } = useParams();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getTransaction(hash);
        setTx(res.data);
      } catch (err) {
        setError('Transaction not found');
        console.error('Error fetching transaction:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hash]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 text-center">
          <div className="data-card p-12">
            <ArrowRightLeft className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Transaction Not Found</h2>
            <p className="text-muted-foreground mb-4">The requested transaction could not be found.</p>
            <Link to="/transactions">
              <Button>View All Transactions</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="tx-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
            <ArrowRightLeft className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Transaction Details</h1>
            <p className="text-sm text-muted-foreground">{timeAgo(tx.timestamp)}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Status"
            value={tx.status === 'confirmed' ? 'Success' : 'Pending'}
            icon={Check}
            accentColor="success"
            valueClassName={tx.status === 'confirmed' ? 'text-green-400' : 'text-yellow-400'}
          />
          <StatCard
            title="Confirmations"
            value={formatNumber(tx.confirmations, 0)}
            icon={Box}
            accentColor="primary"
          />
          <StatCard
            title="Value"
            value={`${formatNumber(tx.value, 6)} GG`}
            icon={ArrowRightLeft}
            accentColor="secondary"
          />
          <StatCard
            title="Fee"
            value={`${tx.fee.toFixed(6)} GG`}
            icon={Fuel}
            accentColor="warning"
          />
        </div>

        {/* Transaction Overview */}
        <div className="data-card p-6 mb-8" data-testid="tx-overview">
          <h2 className="text-lg font-bold mb-4">Transaction Overview</h2>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Transaction Hash</p>
              <HashDisplay hash={tx.hash} truncate={false} className="break-all" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Block</p>
                <Link to={`/block/${tx.block_height}`} className="text-primary hover:underline font-mono">
                  #{formatNumber(tx.block_height, 0)}
                </Link>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Block Hash</p>
                <HashDisplay hash={tx.block_hash} link={`/block/${tx.block_height}`} />
              </div>
            </div>
            
            <div>
              <p className="text-xs text-muted-foreground mb-1">Timestamp</p>
              <p className="font-mono text-sm">{formatDate(tx.timestamp)}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground">Type</p>
              <Badge variant="outline" className={getTxTypeColor(tx.type)}>
                {tx.type}
              </Badge>
            </div>
          </div>
        </div>

        {/* Transfer Details */}
        <div className="data-card p-6 mb-8" data-testid="transfer-details">
          <h2 className="text-lg font-bold mb-4">Transfer Details</h2>
          
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4 rounded-xl bg-white/5">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">From</p>
              <HashDisplay hash={tx.from_address} type="address" link={`/address/${tx.from_address}`} truncate={false} />
            </div>
            
            <div className="hidden md:flex items-center justify-center w-12">
              <div className="p-2 rounded-full bg-primary/20">
                <ArrowRight className="w-4 h-4 text-primary" />
              </div>
            </div>
            
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">To</p>
              <HashDisplay hash={tx.to_address} type="address" link={`/address/${tx.to_address}`} truncate={false} />
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Value</p>
              <p className="text-lg font-bold text-primary">{formatNumber(tx.value, 6)} GG</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Transaction Fee</p>
              <p className="font-mono">{tx.fee.toFixed(6)} GG</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Gas Price</p>
              <p className="font-mono">{tx.gas_price} Gwei</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Gas Used</p>
              <p className="font-mono">{formatNumber(tx.gas_used, 0)}</p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="data-card p-6" data-testid="additional-info">
          <h2 className="text-lg font-bold mb-4">Additional Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Nonce</p>
              <p className="font-mono">{tx.nonce}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Position in Block</p>
              <p className="font-mono">{tx.index}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
